/**
 * PixiPlayGrid — Grid container managing PixiSymbolCell instances.
 * Handles layout, cell creation/destruction, and GSAP animation triggers.
 */
import { Container, Texture } from 'pixi.js';
import gsap from 'gsap';
import type { CascadeSlotConfig, GridCell, CascadeFallStyle, CascadeSymbolConfig, SymbolAnimationTrigger } from '@core/types/cascade-slot.types';
import { buildGravityKeyframes, buildFillKeyframes, buildRemoveTween, buildRemoveScaleTween, buildWinPulseTween } from '@renderer/pixi/cascadeEasings';
import { PixiSymbolCell } from './PixiSymbolCell';
import type { SpineSymbolManager } from '@renderer/services/cascade-slot/SpineSymbolManager';

export interface GridAnimConfig {
  removeDuration: number;
  fillDuration: number;
  gravityDuration: number;
  columnDelay: number;
  winScale: number;
  maxFallDistance: number;
  fallStyle: CascadeFallStyle;
  skipAnimations: boolean;
}

export class PixiPlayGrid {
  readonly container = new Container();

  private cells: PixiSymbolCell[][] = [];
  private config: CascadeSlotConfig | null = null;
  private cellWidth = 0;
  private cellHeight = 0;
  private spacing = 4;
  private spineManager: SpineSymbolManager | null = null;
  /** Track which symbolId each cell currently shows (for spine recycling) */
  private cellSymbolIds: Map<string, string> = new Map();

  constructor() {}

  /** Initialize/resize the grid layout */
  setup(
    config: CascadeSlotConfig,
    containerWidth: number,
    containerHeight: number,
    spineManager?: SpineSymbolManager,
  ): void {
    this.config = config;
    if (spineManager) this.spineManager = spineManager;
    this.spacing = config.symbolSpacing ?? 4;

    const cols = config.columns;
    const rows = config.rows;

    const availableWidth = containerWidth - this.spacing * (cols - 1);
    const availableHeight = containerHeight - this.spacing * (rows - 1);
    this.cellWidth = Math.max(16, Math.floor(availableWidth / cols));
    this.cellHeight = Math.max(16, Math.floor(availableHeight / rows));

    // Rebuild cells if grid dimensions changed
    const currentRows = this.cells.length;
    const currentCols = currentRows > 0 ? this.cells[0].length : 0;

    if (currentRows !== rows || currentCols !== cols) {
      this.destroyCells();
      this.cells = [];

      for (let r = 0; r < rows; r++) {
        const row: PixiSymbolCell[] = [];
        for (let c = 0; c < cols; c++) {
          const cell = new PixiSymbolCell(this.cellWidth, this.cellHeight);
          cell.container.position.set(
            c * (this.cellWidth + this.spacing),
            r * (this.cellHeight + this.spacing)
          );
          this.container.addChild(cell.container);
          row.push(cell);
        }
        this.cells.push(row);
      }
    } else {
      // Just reposition existing cells
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cell = this.cells[r][c];
          cell.resize(this.cellWidth, this.cellHeight);
          cell.container.position.set(
            c * (this.cellWidth + this.spacing),
            r * (this.cellHeight + this.spacing)
          );
        }
      }
    }
  }

  /** Update grid with new cell data and trigger animations */
  updateGrid(
    grid: GridCell[][],
    textures: Map<string, Texture>,
    animConfig: GridAnimConfig
  ): void {
    if (!this.config) return;

    const symbols = this.config.symbols;
    const reelHeights = this.config.reelHeights;
    const totalRows = this.config.rows;

    const getSymbol = (symbolId: string): CascadeSymbolConfig =>
      symbols.find(s => s.id === symbolId) ?? symbols[0];

    for (let r = 0; r < grid.length && r < this.cells.length; r++) {
      for (let c = 0; c < grid[r].length && c < this.cells[r].length; c++) {
        const gridCell = grid[r][c];
        const pixiCell = this.cells[r][c];

        // Handle variable-height reels: hide cells that are above the reel start
        if (reelHeights && reelHeights[c]) {
          const startRow = totalRows - reelHeights[c];
          if (r < startRow) {
            pixiCell.container.visible = false;
            continue;
          }
        }

        if (!gridCell || !gridCell.symbolId) {
          pixiCell.container.visible = false;
          continue;
        }

        pixiCell.container.visible = true;
        const symbol = getSymbol(gridCell.symbolId);
        const texture = symbol.displayMode === 'sprite' && symbol.textureAssetPath
          ? textures.get(symbol.textureAssetPath) ?? null
          : null;

        // Kill any running tweens on this cell before updating
        gsap.killTweensOf(pixiCell.container);
        gsap.killTweensOf(pixiCell.container.scale);
        pixiCell.resetAnimations();

        // ── Spine instance management ──
        const cellKey = `r${r}c${c}`;
        const prevSymbolId = this.cellSymbolIds.get(cellKey);
        const isSpineSymbol = symbol.displayMode === 'spine' && !!symbol.spineConfig;

        if (isSpineSymbol && this.spineManager && symbol.spineConfig) {
          // Acquire or reuse spine instance
          if (prevSymbolId !== gridCell.symbolId) {
            // Symbol changed — release old, acquire new
            this.spineManager.releaseSpine(cellKey);
            pixiCell.detachSpine();
            const spine = this.spineManager.acquireSpine(cellKey, gridCell.symbolId, symbol.spineConfig);
            if (spine) pixiCell.attachSpine(spine);
          } else if (!this.spineManager.hasSpine(cellKey)) {
            // Same symbol but no spine yet (first render)
            const spine = this.spineManager.acquireSpine(cellKey, gridCell.symbolId, symbol.spineConfig);
            if (spine) pixiCell.attachSpine(spine);
          }
          this.cellSymbolIds.set(cellKey, gridCell.symbolId);
        } else {
          // Not a spine symbol — release if previously was
          if (this.spineManager && this.spineManager.hasSpine(cellKey)) {
            this.spineManager.releaseSpine(cellKey);
            pixiCell.detachSpine();
          }
          this.cellSymbolIds.set(cellKey, gridCell.symbolId);
        }

        // Update visual data
        pixiCell.update(gridCell, symbol, texture);

        // Reset position to layout position (animations may have moved it)
        const layoutY = r * (this.cellHeight + this.spacing);
        const layoutX = c * (this.cellWidth + this.spacing);
        pixiCell.container.position.set(layoutX, layoutY);

        // ── Spine animation triggers ──
        if (isSpineSymbol && this.spineManager && gridCell.animationTrigger) {
          this.spineManager.playAnimation(cellKey, gridCell.animationTrigger);
        }

        if (animConfig.skipAnimations) continue;

        // Trigger animations based on cell state
        const colDelay = (gridCell.columnIndex * animConfig.columnDelay) / 1000;

        if (gridCell.isWinning) {
          // For spine symbols, spine animation handles win; for others, use GSAP pulse
          if (!isSpineSymbol) {
            gsap.to(pixiCell.container.scale, buildWinPulseTween(animConfig.winScale));
          }
        } else if (gridCell.isRemoving) {
          // Stop spine animation during removal, let GSAP handle the fade
          if (isSpineSymbol && this.spineManager) {
            this.spineManager.stopAnimation(cellKey);
          }
          gsap.to(pixiCell.container, buildRemoveTween(layoutY, animConfig.removeDuration, colDelay));
          gsap.to(pixiCell.container.scale, buildRemoveScaleTween(animConfig.removeDuration, colDelay));
        } else if (gridCell.isFalling) {
          const distRatio = animConfig.maxFallDistance > 0
            ? gridCell.fallDistance / animConfig.maxFallDistance
            : 1;
          const duration = animConfig.gravityDuration * Math.max(0.4, distRatio);
          const fallOffset = gridCell.fallDistance * (this.cellHeight + this.spacing);
          const startY = layoutY - fallOffset;

          // Start from offset position
          pixiCell.container.y = startY;

          const tweenVars = buildGravityKeyframes(
            startY,
            layoutY,
            this.cellHeight,
            duration,
            animConfig.fallStyle,
            colDelay
          );
          gsap.to(pixiCell.container, tweenVars);
        } else if (gridCell.isNew) {
          // New symbols enter from above the grid
          const entryY = -(this.cellHeight + this.spacing);
          pixiCell.container.y = entryY;

          const tweenVars = buildFillKeyframes(
            entryY,
            layoutY,
            this.cellHeight,
            animConfig.fillDuration,
            animConfig.fallStyle,
            colDelay
          );
          gsap.to(pixiCell.container, tweenVars);
        }
      }
    }
  }

  // ── Spine Animation Orchestration ─────────────────────────────

  /**
   * Orchestrate prepare→match animation sequence on winning cells.
   * Groups wins by column, staggers prepare triggers, then plays match simultaneously.
   */
  triggerWinAnimations(
    winPositions: Array<{ row: number; col: number }>,
    columnDelay: number,
  ): void {
    if (!this.spineManager || winPositions.length === 0) return;

    // Group by column for staggered prepare
    const byCol = new Map<number, Array<{ row: number; col: number }>>();
    for (const pos of winPositions) {
      const arr = byCol.get(pos.col) ?? [];
      arr.push(pos);
      byCol.set(pos.col, arr);
    }

    const sortedCols = Array.from(byCol.keys()).sort((a, b) => a - b);
    const delayMs = columnDelay;

    // Phase 1: Staggered prepare
    for (let i = 0; i < sortedCols.length; i++) {
      const col = sortedCols[i];
      const positions = byCol.get(col)!;
      const delay = i * delayMs;

      setTimeout(() => {
        for (const pos of positions) {
          const cellKey = `r${pos.row}c${pos.col}`;
          this.spineManager?.playAnimation(cellKey, 'prepare', false);
        }
      }, delay);
    }

    // Phase 2: After all prepares, trigger simultaneous match
    const totalPrepareTime = sortedCols.length * delayMs + 500; // 500ms for prepare anim
    setTimeout(() => {
      for (const pos of winPositions) {
        const cellKey = `r${pos.row}c${pos.col}`;
        this.spineManager?.playAnimation(cellKey, 'match', false);
      }
    }, totalPrepareTime);
  }

  /**
   * Trigger idle animations on wild/scatter spine symbols.
   */
  triggerIdleAnimations(): void {
    this.spineManager?.triggerIdleAnimations();
  }

  // ── Cleanup ─────────────────────────────────────────────────────

  /** Stop all active animations and reset cells */
  stopAllAnimations(): void {
    // Stop spine animations
    this.spineManager?.stopAllAnimations();

    for (const row of this.cells) {
      for (const cell of row) {
        gsap.killTweensOf(cell.container);
        gsap.killTweensOf(cell.container.scale);
        cell.resetAnimations();
      }
    }
  }

  destroy(): void {
    this.stopAllAnimations();
    this.destroyCells();
    this.container.destroy({ children: true });
  }

  private destroyCells(): void {
    // Release all spine instances back to pool before destroying cells
    if (this.spineManager) {
      for (let r = 0; r < this.cells.length; r++) {
        for (let c = 0; c < this.cells[r].length; c++) {
          const cellKey = `r${r}c${c}`;
          this.cells[r][c].detachSpine();
          this.spineManager.releaseSpine(cellKey);
        }
      }
    }
    this.cellSymbolIds.clear();

    for (const row of this.cells) {
      for (const cell of row) {
        gsap.killTweensOf(cell.container);
        gsap.killTweensOf(cell.container.scale);
        cell.destroy();
      }
    }
    this.cells = [];
  }
}
